import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import { getAuth, Auth, signInWithCustomToken, User, signOut as firebaseSignOut, onAuthStateChanged, Unsubscribe } from 'firebase/auth';

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// Initialize Firebase
let app: FirebaseApp;
if (getApps().length === 0) {
  app = initializeApp(firebaseConfig);
} else {
  app = getApps()[0];
}

// Get Auth instance
export const auth: Auth = getAuth(app);

/**
 * Sign in with Firebase custom token and get the JWT token
 * @param customToken The custom token received from OTP verification
 * @returns The JWT token (ID token) for API authentication
 */
export async function signInWithCustomTokenAndGetJWT(customToken: string): Promise<string> {
  try {
    // Sign in with the custom token
    const userCredential = await signInWithCustomToken(auth, customToken);
    const user: User = userCredential.user;

    // Get the JWT token (ID token) - this automatically refreshes if expired
    const idToken = await user.getIdToken();
    
    return idToken;
  } catch (error: any) {
    console.error('Error signing in with custom token:', error);
    throw new Error(error.message || 'Failed to sign in with Firebase');
  }
}

/**
 * Get the current authenticated user
 * @returns The current Firebase user or null if not authenticated
 */
export function getCurrentUser(): User | null {
  return auth.currentUser;
}

/**
 * Get a fresh JWT token from the current authenticated user
 * This automatically refreshes the token if it's expired
 * @returns The JWT token or null if no user is authenticated
 */
export async function getCurrentUserToken(): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) {
    return null;
  }
  try {
    // getIdToken() automatically refreshes expired tokens
    return await user.getIdToken();
  } catch (error: any) {
    console.error('Error getting user token:', error);
    return null;
  }
}

/**
 * Sign out from Firebase
 */
export async function signOut(): Promise<void> {
  try {
    await firebaseSignOut(auth);
  } catch (error: any) {
    console.error('Error signing out from Firebase:', error);
    throw new Error(error.message || 'Failed to sign out from Firebase');
  }
}

/**
 * Listen to Firebase authentication state changes
 * @param callback Function to call when auth state changes
 * @returns Unsubscribe function
 */
export function onAuthStateChange(callback: (user: User | null) => void): Unsubscribe {
  return onAuthStateChanged(auth, callback);
}

/**
 * Extract a custom claim from a JWT token
 * @param token The JWT token
 * @param claimName The name of the custom claim to extract
 * @returns The claim value or null if not found
 */
export function extractClaimFromToken(token: string, claimName: string): string | null {
  try {
    // JWT tokens have three parts: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('Invalid JWT token format');
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];
    
    // Base64URL decode (replace URL-safe characters and add padding if needed)
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - base64.length % 4) % 4);
    
    // Decode and parse JSON
    const decoded = JSON.parse(atob(padded));
    
    // Extract the custom claim
    return decoded[claimName] || null;
  } catch (error) {
    console.error('Error extracting claim from token:', error);
    return null;
  }
}

export default app;

